import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";

// Soft UI components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

import liveCricketLogo from "assets/images/livecricketkerala.png";
import "../../playerpdf.css";

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return String(value).trim() !== "";
};

const isIconPlayer = (isIcon) => {
  if (isIcon === null || isIcon === undefined) return false;
  if (typeof isIcon === "boolean") return isIcon === true;
  if (typeof isIcon === "string") {
    const trimmed = isIcon.trim().toLowerCase();
    return trimmed === "1" || trimmed === "true" || trimmed === "yes";
  }
  return Boolean(isIcon);
};

function PlayerPdf() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [searchName, setSearchName] = useState("");
  const [searchNumber, setSearchNumber] = useState("");
  const [showAll, setShowAll] = useState(true);
  const touchRef = useRef({ x: 0, y: 0, time: 0 });
  const [returnToAll, setReturnToAll] = useState(false);
  const listScrollRef = useRef(0);
  const [showSplash, setShowSplash] = useState(true);

  // 2-second splash on load
  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const search = window.location.search || "";
    const params = new URLSearchParams(search);
    const u = params.get("u");

    if (!u) {
      setError("Missing parameter. Please provide ?u=<code>.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const apiUrl = `${global.config.app.backend_api_url}share_player_cards/get_player_json?u=${encodeURIComponent(u)}`;

    axios
      .get(apiUrl)
      .then((res) => {
        const payload = res && res.data ? res.data : res;
        setData(payload || {});
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load player PDF JSON", err);
        setError("Unable to load player book. Please check the link or try again later.");
        setLoading(false);
      });
  }, []);

  const pages = useMemo(() => {
    if (!data) return [];

    const result = [];

    // Main players
    if (Array.isArray(data.players)) {
      data.players.forEach((player, idx) => {
        result.push({ type: "player", player, idx });
      });
    }

    // Waiting list
    if (Array.isArray(data.waiting_players) && data.waiting_players.length) {
      result.push({ type: "waiting-title" });
      data.waiting_players.forEach((player, idx) => {
        result.push({ type: "waiting-player", player, idx });
      });
    }

    // Summary page for missing slots
    if (hasValue(data.missing_sl)) {
      result.push({ type: "summary" });
    }

    return result;
  }, [data]);

  const searchablePlayers = useMemo(() => {
    if (!pages || !pages.length) return [];

    const list = [];
    pages.forEach((page, idx) => {
      if (page.type === "player" || page.type === "waiting-player") {
        const player = page.player || {};
        const slNo = hasValue(player.sl_no) ? String(player.sl_no) : "";
        const slLabel =
          page.type === "waiting-player" && slNo ? `W${slNo}` : slNo;

        list.push({
          type: page.type,
          player,
          pageIndex: idx,
          slLabel,
        });
      }
    });

    return list;
  }, [pages]);

  const totalPages = pages.length || 0;
  const currentPage = totalPages > 0 ? pages[pageIndex] : null;

  const styling = (data && data.styling) || {};
  const assets = (data && data.assets) || {};
  const series = (data && data.series) || {};
  const main = (data && data.main) || {};

  const primaryNameBg = styling.Player_Name_Bg_Color || "#9a49f3";
  const primaryNameFont = styling.Player_Name_Font_Color || "#ffffff";
  const mobileBg = styling.Player_Mobile_No_Bg_Color || "#333333";
  const mobileFont = styling.Player_Mobile_No_Font_Color || "#ffffff";
  const iconBg = styling.Player_Icon_Bg_Color || "#777777";
  const iconFont = styling.Player_Icon_Font_Color || "#333333";
  const statFont = styling.Player_Cric_Stat_Font_Color || "#ffffff";
  const statLite = styling.Player_Cric_Stat_Bg_Color_Lite || "#7e2117";
  const statDark = styling.Player_Cric_Stat_Bg_Color_Darker || "#922a1d";

  const includeCricDetails =
    data && data.series && typeof data.series.include_cric_details !== "undefined"
      ? Number(data.series.include_cric_details) === 1
      : data && typeof data.include_cric_details !== "undefined"
      ? Number(data.include_cric_details) === 1
      : true;

  const filteredPlayers = useMemo(() => {
    if (!searchablePlayers.length) return [];

    const nameQuery = searchName.trim().toLowerCase();
    const numberQuery = searchNumber.trim().toLowerCase();

    if (!nameQuery && !numberQuery) {
      return searchablePlayers;
    }

    return searchablePlayers.filter(({ player, slLabel }) => {
      const fullName = (player.full_name || "").toLowerCase();
      const matchesName = nameQuery ? fullName.includes(nameQuery) : true;

      const slText = slLabel.toLowerCase();
      const matchesNumber = numberQuery
        ? slText.includes(numberQuery) ||
          String(player.sl_no || "").toLowerCase().includes(numberQuery)
        : true;

      return matchesName && matchesNumber;
    });
  }, [searchName, searchNumber, searchablePlayers]);

  const handleNavigate = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= totalPages) return;
    setDirection(nextIndex > pageIndex ? "forward" : "backward");
    setPageIndex(nextIndex);
  };

  const handlePrev = () => handleNavigate(pageIndex - 1);
  const handleNext = () => handleNavigate(pageIndex + 1);

  const handleJumpTo = (targetIndex, fromAll = false) => {
    if (targetIndex === pageIndex) return;
    if (fromAll) {
      listScrollRef.current = window.scrollY || 0;
      setReturnToAll(true);
    }
    setShowAll(false);
    handleNavigate(targetIndex);
  };

  const handleCloseToAll = () => {
    setShowAll(true);
    setReturnToAll(false);
    window.scrollTo(0, listScrollRef.current || 0);
  };

  // Keyboard navigation (left/right arrows)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showAll || loading || !!error || totalPages === 0) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handlePrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showAll, loading, error, totalPages, pageIndex]);

  // Mobile swipe navigation
  const handleTouchStart = (event) => {
    if (!event.touches || event.touches.length === 0) return;
    const touch = event.touches[0];
    touchRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  };

  const handleTouchEnd = (event) => {
    if (showAll || loading || !!error || totalPages === 0) return;
    if (!touchRef.current) return;

    const start = touchRef.current;
    const touch =
      (event.changedTouches && event.changedTouches[0]) || event.touches?.[0];
    if (!touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const dt = Date.now() - start.time;

    const minDistance = 40;
    const maxTime = 600;

    if (dt > maxTime) return;
    if (Math.abs(dx) < minDistance) return;
    if (Math.abs(dx) < Math.abs(dy) * 1.5) return;

    if (dx > 0) {
      handlePrev();
    } else {
      handleNext();
    }
  };

  const renderCover = () => {
    const title = series.series_name || main.auction_name || "Player Book";
    const dateText =
      (series.match_date ? `Match Date: ${series.match_date}` : "") +
      (series.from_time ? ` • From ${series.from_time}` : "");

    return (
      <div className="playerpdf-page-inner playerpdf-cover">
        <div className="playerpdf-cover-header">
          {assets.league_logo_header_img ? (
            <img
              src={assets.league_logo_header_img}
              alt="League header"
              className="playerpdf-cover-logo"
            />
          ) : null}

          {assets.league_logo ? (
            <div className="playerpdf-cover-badge">
              <img
                src={assets.league_logo}
                alt="League logo"
                className="playerpdf-cover-badge-img"
              />
            </div>
          ) : null}
        </div>

        <div className="playerpdf-cover-body">
          <SoftTypography
            variant="h3"
            fontWeight="bold"
            className="playerpdf-cover-title"
          >
            {title}
          </SoftTypography>

          

          {hasValue(main.base_price) && (
            <div className="playerpdf-cover-chip-row">
              <div className="playerpdf-cover-chip">
                Base Price:{" "}
                <strong>
                  {main.base_price} {main.currency || "INR"}
                </strong>
              </div>
            </div>
          )}

          {Array.isArray(data.teams) && data.teams.length > 0 && (
            <div className="playerpdf-cover-teams">
              <SoftTypography
                variant="subtitle2"
                className="playerpdf-cover-teams-label"
              >
                Participating Teams
              </SoftTypography>
              <div className="playerpdf-cover-team-list">
                {data.teams.map((t) => (
                  <span key={t.team_id} className="playerpdf-cover-team-pill">
                    {t.team_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="playerpdf-cover-footer">
          <SoftTypography variant="caption" color="textSecondary">
            {series.footer_description || "Powered by 4CR.in"}
          </SoftTypography>
          {hasValue(series.pdf_version_no) && (
            <SoftTypography
              variant="caption"
              className="playerpdf-cover-version"
            >
              PDF Version #{series.pdf_version_no}
            </SoftTypography>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerCore = (player, isWaiting) => {
    if (!player) return null;

    const hasStats =
      includeCricDetails &&
      (hasValue(player.cric_matches) ||
        hasValue(player.cric_runs) ||
        hasValue(player.cric_wickets) ||
        hasValue(player.cric_batting_style) ||
        hasValue(player.cric_bowling_style));

    const additionalFields = [];

    if (includeCricDetails) {
      if (hasValue(player.role)) {
        additionalFields.push(`Role: ${player.role}`);
      }
      if (hasValue(player.keeping_flag)) {
        additionalFields.push(`Keeping: ${player.keeping_flag}`);
      }
      if (hasValue(player.district_name)) {
        additionalFields.push(`District: ${player.district_name}`);
      }
      if (hasValue(player.office_location)) {
        additionalFields.push(`Office Location: ${player.office_location}`);
      }
      if (hasValue(player.foreign_player_details)) {
        additionalFields.push(
          `Foreign Player (RO): ${player.foreign_player_details}`
        );
      }
      if (hasValue(player.previous_team_details)) {
        additionalFields.push(
          `${isWaiting ? "Previous Team" : "Current Club"}: ${
            player.previous_team_details
          }`
        );
      }
    }

    return (
      <>
        <div className="playerpdf-player-main">
          <div className="playerpdf-player-left">
            <div className="playerpdf-slno-badge">
              {isWaiting ? `W${player.sl_no}` : player.sl_no}
            </div>

            <div
              className="playerpdf-name-pill"
              style={{
                background: primaryNameBg,
                color: primaryNameFont,
              }}
            >
              {(player.full_name || "").toUpperCase()}
            </div>

            <div
              className="playerpdf-mobile-pill"
              style={{ background: mobileBg, color: mobileFont }}
            >
              <img
                src="https://4cr.in/otherstatic/5296520_bubble_chat_mobile_whatsapp_whatsapp logo_icon.png"
                alt="WhatsApp"
                className="playerpdf-mobile-icon"
              />
              {player.mobile_no}
            </div>

            {isIconPlayer(player.is_icon) && (
              <div
                className="playerpdf-icon-pill"
                style={{ background: iconBg, color: iconFont }}
              >
                <img
                  src="https://4cr.in/otherstatic/king.png"
                  alt="Icon"
                  className="playerpdf-icon-image"
                />
                <span>
                  OWNER/ICON
                  {hasValue(player.icon_text) ? `: ${player.icon_text}` : ""}
                </span>
              </div>
            )}
          </div>

          <div className="playerpdf-player-right">
            <div className="playerpdf-photo-frame">
              {player.profile_pic ? (
                <img
                  src={player.profile_pic}
                  alt={player.full_name || "Player"}
                  className="playerpdf-photo"
                />
              ) : (
                <div className="playerpdf-photo-placeholder">
                  <SoftTypography variant="button" color="text">
                    No Photo
                  </SoftTypography>
                </div>
              )}
            </div>
          </div>
        </div>

        {hasStats && (
          <div
            className="playerpdf-stats-row"
            style={{ color: statFont }}
          >
            <div
              className="playerpdf-stats-cell playerpdf-stats-label"
              style={{ background: statDark }}
            >
              Style
            </div>
            <div
              className="playerpdf-stats-cell playerpdf-stats-style"
              style={{ background: statLite }}
            >
              {hasValue(player.cric_batting_style) && (
                <>
                  <img
                    src="https://4cr.in/otherstatic/cricket_bat.png"
                    alt="Bat"
                    className="playerpdf-stats-icon"
                  />
                  <span>{player.cric_batting_style}</span>
                </>
              )}
              {hasValue(player.cric_bowling_style) && (
                <>
                  <span className="playerpdf-stats-separator">|</span>
                  <img
                    src="https://4cr.in/otherstatic/cricket_ball.png"
                    alt="Ball"
                    className="playerpdf-stats-icon"
                  />
                  <span>{player.cric_bowling_style}</span>
                </>
              )}
            </div>

            {hasValue(player.cric_matches) && (
              <>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-label"
                  style={{ background: statDark }}
                >
                  Matches
                </div>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-value"
                  style={{ background: statLite }}
                >
                  {player.cric_matches}
                </div>
              </>
            )}

            {hasValue(player.cric_runs) && (
              <>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-label"
                  style={{ background: statDark }}
                >
                  Runs
                </div>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-value"
                  style={{ background: statLite }}
                >
                  {player.cric_runs}
                </div>
              </>
            )}

            {hasValue(player.cric_wickets) && (
              <>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-label"
                  style={{ background: statDark }}
                >
                  Wickets
                </div>
                <div
                  className="playerpdf-stats-cell playerpdf-stats-value"
                  style={{ background: statLite }}
                >
                  {player.cric_wickets}
                </div>
              </>
            )}
          </div>
        )}

        {additionalFields.length > 0 && (
          <div className="playerpdf-extra-row">
            {additionalFields.map((text, idx) => (
              <span key={idx} className="playerpdf-extra-pill">
                {text}
              </span>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderPlayerPage = (player) => {
    return (
      <div className="playerpdf-page-inner">
        {assets.league_pdf_banner || series.series_name ? (
          <div className="playerpdf-banner">
            {assets.league_pdf_banner ? (
              <img
                src={assets.league_pdf_banner}
                alt="Banner"
                className="playerpdf-banner-img"
              />
            ) : (
              <div className="playerpdf-banner-fallback">
                {series.series_name}
              </div>
            )}
          </div>
        ) : null}

        {renderPlayerCore(player, false)}
      </div>
    );
  };

  const renderWaitingTitlePage = () => {
    return (
      <div className="playerpdf-page-inner playerpdf-waiting-title">
        <SoftTypography variant="h3" fontWeight="bold">
          Waiting List
        </SoftTypography>
        <SoftTypography variant="body2" color="textSecondary" mt={1}>
          Players on standby for this series.
        </SoftTypography>
      </div>
    );
  };

  const renderWaitingPlayerPage = (player) => {
    return (
      <div className="playerpdf-page-inner">
        {assets.league_pdf_banner || series.series_name ? (
          <div className="playerpdf-banner">
            {assets.league_pdf_banner ? (
              <img
                src={assets.league_pdf_banner}
                alt="Banner"
                className="playerpdf-banner-img"
              />
            ) : (
              <div className="playerpdf-banner-fallback">
                {series.series_name}
              </div>
            )}
          </div>
        ) : null}

        {renderPlayerCore(player, true)}
      </div>
    );
  };

  const renderSummaryPage = () => {
    return (
      <div className="playerpdf-page-inner playerpdf-summary">
        <SoftTypography variant="h4" fontWeight="bold" mb={2}>
          Summary
        </SoftTypography>

        {hasValue(data.missing_sl) && (
          <div className="playerpdf-summary-card">
            <SoftTypography variant="subtitle2" fontWeight="bold">
              Free Slots
            </SoftTypography>
            <SoftTypography variant="body2" mt={0.5}>
              {data.missing_sl}
            </SoftTypography>
          </div>
        )}

        <div className="playerpdf-summary-footer">
          <img
            src="https://4cr.in/otherstatic/live_cricket_kerala.png"
            alt="Live Cricket Kerala"
            className="playerpdf-summary-logo"
          />
        </div>
      </div>
    );
  };

  const backgroundStyle = assets.league_pdf_background
    ? {
        backgroundImage: `url(${assets.league_pdf_background})`,
      }
    : {};

  const splashMessage =
    data?.series?.series_name
      ? `Generating ${data.series.series_name} players pdf view`
      : "Generating players pdf view...";

  return (
    <div className="playerpdf-root" style={backgroundStyle}>
      {showSplash && (
        <div className="playerpdf-splash">
          <div className="playerpdf-splash-inner">
            <img
              src={liveCricketLogo}
              alt="Live Cricket Kerala"
              className="playerpdf-splash-logo"
            />
            <div className="playerpdf-splash-spinner" />
            <SoftTypography variant="h5" fontWeight="bold" className="playerpdf-splash-text">
              {splashMessage}
            </SoftTypography>
          </div>
        </div>
      )}
      <Helmet>
        <title>
          {series.series_name
            ? `${series.series_name} - Player Book`
            : "Player Book"}
        </title>
        <meta
          name="description"
          content="Flip through players and waiting list in an interactive book view."
        />
      </Helmet>

      <div className="playerpdf-overlay">
        <div className="playerpdf-topbar">
          <div className="playerpdf-topbar-left">
            <img
              src={assets.league_logo || liveCricketLogo}
              alt={assets.league_logo ? "League logo" : "Live Cricket Kerala"}
              className="playerpdf-topbar-logo"
            />
            <div>
              <SoftTypography variant="button" fontWeight="bold">
                {series.series_name || "Player Book"}
              </SoftTypography>
              
            </div>
          </div>

          {totalPages > 0 && (
            <div className="playerpdf-topbar-right">
              <SoftTypography variant="caption" color="textSecondary">
                Page {pageIndex + 1} of {totalPages}
              </SoftTypography>
              {!showAll && returnToAll && (
                <SoftButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={handleCloseToAll}
                  className="playerpdf-close-button"
                >
                  Close
                </SoftButton>
              )}
            </div>
          )}
        </div>

        {totalPages > 0 && (
          <div className="playerpdf-searchbar">
            <div className="playerpdf-search-fields">
              <SoftInput
                placeholder="Search by player name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                size="small"
              />
              <SoftInput
                placeholder="Search by SL / PDF No"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                size="small"
              />
            </div>
            <div className="playerpdf-search-actions">
              <SoftButton
                variant="text"
                color="secondary"
                size="small"
                onClick={() => {
                  setSearchName("");
                  setSearchNumber("");
                }}
              >
                Clear
              </SoftButton>
              <SoftButton
                variant={showAll ? "outlined" : "gradient"}
                color="info"
                size="small"
                onClick={() => {
                  setReturnToAll(false);
                  setShowAll((prev) => !prev);
                }}
              >
                {showAll ? "Book View" : "Show All"}
              </SoftButton>
            </div>
          </div>
        )}

        {totalPages > 0 &&
          (searchName.trim() !== "" || searchNumber.trim() !== "") &&
          !loading &&
          !error &&
          filteredPlayers.length > 0 && (
            <div className="playerpdf-search-results">
              {filteredPlayers.map((item, idx) => (
                <button
                  key={`${item.type}-${item.player.sl_no}-${idx}`}
                  type="button"
                  className="playerpdf-search-item"
                  onClick={() => handleJumpTo(item.pageIndex, showAll)}
                >
                  <span className="playerpdf-search-item-sl">
                    {item.slLabel || "-"}
                  </span>
                  <span className="playerpdf-search-item-name">
                    {item.player.full_name || "Unnamed"}
                  </span>
                  {item.type === "waiting-player" && (
                    <span className="playerpdf-search-item-tag">W</span>
                  )}
                </button>
              ))}
            </div>
          )}

        <div
          className="playerpdf-book"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {loading && (
            <div className="playerpdf-state">
              <div className="playerpdf-spinner" />
              <SoftTypography variant="body2" mt={1}>
                Loading player book...
              </SoftTypography>
            </div>
          )}

          {!loading && error && (
            <div className="playerpdf-state">
              <SoftTypography variant="body2" color="error" mb={1}>
                {error}
              </SoftTypography>
            </div>
          )}

          {!loading && !error && totalPages === 0 && (
            <div className="playerpdf-state">
              <SoftTypography variant="body2">
                No pages to display for this link.
              </SoftTypography>
            </div>
          )}

          {!loading && !error && totalPages > 0 && showAll && (
            <div className="playerpdf-all-grid">
              {(searchName.trim() || searchNumber.trim()
                ? filteredPlayers
                : searchablePlayers
              ).map((item, idx) => (
                <button
                  key={`${item.type}-grid-${item.player.sl_no}-${idx}`}
                  type="button"
                  className="playerpdf-all-card"
                  onClick={() => handleJumpTo(item.pageIndex, true)}
                >
                  <div className="playerpdf-all-card-header">
                    <span className="playerpdf-all-card-sl">
                      {item.slLabel || "-"}
                    </span>
                    {item.type === "waiting-player" && (
                      <span className="playerpdf-all-card-tag">Waiting</span>
                    )}
                  </div>
                  <div className="playerpdf-all-card-main">
                    {item.player.profile_pic && (
                      <div className="playerpdf-all-card-thumb-wrapper">
                        <img
                          src={item.player.profile_pic}
                          alt={item.player.full_name || "Player"}
                          className="playerpdf-all-card-thumb"
                        />
                      </div>
                    )}
                    <div className="playerpdf-all-card-text">
                      <div className="playerpdf-all-card-name">
                        {item.player.full_name || "Unnamed"}
                      </div>
                      {hasValue(item.player.role) && (
                        <div className="playerpdf-all-card-role">
                          {item.player.role}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!loading && !error && currentPage && !showAll && (
            <div
              key={pageIndex}
              className={`playerpdf-page playerpdf-page-${direction}`}
            >
              {currentPage.type === "cover" && renderCover()}
              {currentPage.type === "player" &&
                renderPlayerPage(currentPage.player)}
              {currentPage.type === "waiting-title" &&
                renderWaitingTitlePage()}
              {currentPage.type === "waiting-player" &&
                renderWaitingPlayerPage(currentPage.player)}
              {currentPage.type === "summary" && renderSummaryPage()}
            </div>
          )}
        </div>

        {totalPages > 0 && !showAll && (
          <SoftBox
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            className="playerpdf-controls"
          >
            <SoftButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handlePrev}
              disabled={pageIndex === 0}
            >
              Previous
            </SoftButton>

            <SoftTypography
              variant="caption"
              color="textSecondary"
              className="playerpdf-page-indicator"
            >
              Swipe / click to turn pages
            </SoftTypography>

            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              onClick={handleNext}
              disabled={pageIndex === totalPages - 1}
            >
              Next
            </SoftButton>
          </SoftBox>
        )}
      </div>
    </div>
  );
}

export default PlayerPdf;

